<template>
  <section class="w-full py-16 md:py-20 lg:py-24 bg-white">
    <div class="max-w-[1440px] mx-auto px-6 md:px-8 lg:px-12">
      <!-- Header Section -->
      <div ref="headerRef" class="text-center mb-12 md:mb-16 opacity-0 translate-y-8">
        <!-- Title with blue dot -->
        <div class="flex items-center justify-center gap-3 mb-4">
          <div class="w-3 h-3 rounded-full bg-[#0A2F70]"></div>
                   <!-- Titre - Mobile -->
          <H3Title className="md:hidden text-2xl sm:text-3xl font-extrabold text-[#0F7F5C] uppercase tracking-tight">
            VOS GARANTIES DE SÉCURITÉ
          </H3Title>
          
          <!-- Titre - Desktop -->
          <H2Title className="hidden md:block text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0F7F5C] uppercase tracking-tight">
            VOS GARANTIES DE SÉCURITÉ
          </H2Title>
        </div>
        
        <!-- Subtitle - Mobile -->
        <PContent className="md:hidden text-sm sm:text-base text-gray-700 font-normal max-w-2xl mx-auto">
          Un système sécurisé et confidentiel pour protéger votre démarche citoyenne
        </PContent>
        
        <!-- Subtitle - Desktop -->
        <H4Title className="hidden md:block text-base md:text-lg lg:text-xl text-gray-700 font-normal max-w-2xl mx-auto">
          Un système sécurisé et confidentiel pour protéger votre démarche citoyenne
        </H4Title>
      </div>

      <!-- Cards Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
        <div
          v-for="(feature, index) in features"
          :key="index"
          class="feature-card group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-2 hover:border-[#0F7F5C]/30 transition-all duration-500 p-6 md:p-8 flex flex-col items-center text-center overflow-hidden relative"
          :ref="(el) => setCardRef(el, index)"
        >
          <!-- Decorative Background Shape -->
          <div class="absolute -right-6 -top-6 w-24 h-24 bg-[#0A2F70]/5 rounded-full blur-2xl group-hover:bg-[#0F7F5C]/10 transition-colors duration-500"></div>

          <div class="relative z-10 flex flex-col items-center">
            <div class="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-[#E5E9EF] flex items-center justify-center mb-4 md:mb-6 group-hover:bg-[#0A2F70]/5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
              <component :is="feature.icon" class="w-8 h-8 md:w-10 md:h-10 text-[#0A2F70]" />
            </div>
            <h3 class="text-lg md:text-xl font-bold text-[#0A2F70] mb-3 md:mb-4 group-hover:text-[#0F7F5C] transition-colors duration-300">
              {{ feature.title }}
            </h3>
            <p class="text-sm md:text-base text-gray-700 leading-relaxed">
              {{ feature.description }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { H2Title, H3Title, H4Title, PContent } from '@/components/atoms/text'
import { Shield, Lock, Eye, FileCheck } from 'lucide-vue-next'

defineOptions({
  name: 'FeatureSection'
})

const features = [
  {
    title: 'Protection des données',
    description: 'Vos informations sont cryptées et stockées sur des serveurs sécurisés conformes aux normes internationales.',
    icon: Shield
  },
  {
    title: 'Confidentialité garantie',
    description: 'Possibilité de signaler de manière anonyme. Votre identité reste protégée tout au long du processus.',
    icon: Lock
  },
  {
    title: 'Transparence totale',
    description: 'Suivez l\'évolution de votre signalement en temps réel avec votre numéro de suivi unique.',
    icon: Eye
  },
  {
    title: 'Traitement impartial',
    description: 'Chaque signalement est examiné de manière objective par des équipes formées et indépendantes.',
    icon: FileCheck
  }
]

const headerRef = ref<HTMLElement | null>(null)
const cardRefs = ref<HTMLElement[]>([])
let observer: IntersectionObserver | null = null

const setCardRef = (el: any, index: number) => {
  if (!el) return
  cardRefs.value[index] = el as HTMLElement
}

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        if (entry.target === headerRef.value) {
          gsap.to(headerRef.value, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out'
          })
          observer?.unobserve(headerRef.value)
        } else {
          const index = cardRefs.value.findIndex((card) => card === entry.target)
          if (index >= 0) {
            gsap.to(entry.target, {
              opacity: 1,
              y: 0,
              scale: 1,
              duration: 0.8,
              delay: index * 0.1,
              ease: 'back.out(1.2)',
              force3D: true
            })
            observer?.unobserve(entry.target)
          }
        }
      })
    },
    {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px',
    }
  )

  if (headerRef.value) {
    gsap.set(headerRef.value, { opacity: 0, y: 30 })
    observer.observe(headerRef.value)
  }

  cardRefs.value.forEach((card) => {
    if (card) {
      gsap.set(card, { opacity: 0, y: 40, scale: 0.95 })
      observer?.observe(card)
    }
  })
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
})
</script>

<style scoped>
.feature-card {
  will-change: transform, opacity, box-shadow;
}

@media (prefers-reduced-motion: reduce) {
  .feature-card {
    opacity: 1 !important;
    transform: none !important;
    transition: none !important;
  }
}
</style>
